import { useEffect, useMemo, useState } from 'react';
import {
  Building2,
  Database,
  Palette,
  Receipt,
  Save,
  Settings as SettingsIcon,
  ShieldAlert,
  ShieldCheck,
  Sliders,
  Table2,
  UserCog,
  Users,
  Wallet,
} from 'lucide-react';
import { Helper, PageHead } from '../components/bits.jsx';
import PaymentProcessorsCard from '../components/PaymentProcessorsCard.jsx';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  Card,
  CardBody,
  CardHead,
  EmptyState,
  Field,
  Input,
  KeyValue,
  Loading,
  Modal,
  Select,
  Stat,
  Switch,
  Textarea,
  cx,
  useToast,
} from '../components/ui.jsx';
import { api } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useApi } from '../lib/hooks.js';
import { money, num, relative, titleCase } from '../lib/format.js';

const SECTIONS = [
  { key: 'company', label: 'Our details', icon: Building2 },
  { key: 'ops', label: 'How we work', icon: Sliders },
  { key: 'pay', label: 'Paying lumpers', icon: Wallet },
  { key: 'billing', label: 'Charging customers', icon: Receipt },
  { key: 'rates', label: 'Our price list', icon: Table2 },
  { key: 'brand', label: 'Colours', icon: Palette },
  { key: 'team', label: 'Office Staff Users', icon: UserCog },
  { key: 'account', label: 'Your login', icon: ShieldCheck },
  { key: 'data', label: 'Data', icon: Database },
];

const ROLE_LABELS = {
  owner: 'Owner — full control',
  ops: 'Operations',
  billing: 'Billing & settlements',
  viewer: 'Read-only',
};

export default function Settings() {
  const toast = useToast();
  const [section, setSection] = useState('company');
  const { data, loading, refresh } = useApi('/settings');
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [rateCard, setRateCard] = useState(null);

  useEffect(() => {
    if (data?.settings) setForm(data.settings);
  }, [data]);

  const dirty = useMemo(
    () => Object.entries(form).filter(([key, value]) => String((data?.settings || {})[key] ?? '') !== String(value ?? '')),
    [form, data],
  );

  if (loading && !data) {
    return (
      <>
        <PageHead title="Settings" subtitle="Loading workspace settings…" />
        <Loading rows={7} />
      </>
    );
  }

  const settings = data?.settings || {};
  const rateCards = data?.rateCards || [];
  const counts = data?.counts || {};
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const save = async () => {
    if (!dirty.length) return;
    setBusy(true);
    try {
      await api.put('/settings', Object.fromEntries(dirty));
      toast.ok(`${dirty.length} setting${dirty.length > 1 ? 's' : ''} saved`);
      refresh();
    } catch (err) {
      toast.err('Could not save settings', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHead
        title="Settings"
        subtitle="Our details, how we work, what we charge, and who can use this."
        icon={SettingsIcon}
        actions={
          <>
            {dirty.length ? <Badge tone="amber">{dirty.length} unsaved</Badge> : <Badge tone="green" size="sm" dot>Saved</Badge>}
            <Button variant="primary" size="sm" icon={Save} loading={busy} disabled={!dirty.length} onClick={save}>
              Save changes
            </Button>
          </>
        }
      />

      <div className="settings-layout">
        <nav className="settings-nav">
          {SECTIONS.map((item) => (
            <button
              key={item.key}
              type="button"
              className={cx(section === item.key && 'active')}
              onClick={() => setSection(item.key)}
            >
              <span className="row gap-10">
                <item.icon size={15} strokeWidth={2} />
                {item.label}
              </span>
            </button>
          ))}
        </nav>

        <div className="col gap-20">
          {section === 'company' ? (
            <Card>
              <CardHead title="Our details" subtitle="This is what customers and lumpers see on paperwork" icon={Building2} />
              <CardBody>
                <div className="grid grid-2" style={{ gap: 16 }}>
                  <Field label="What we are called">
                    <Input value={form['company.name'] || ''} onChange={(e) => set('company.name', e.target.value)} />
                  </Field>
                  <Field label="Full company name">
                    <Input value={form['company.legal'] || ''} onChange={(e) => set('company.legal', e.target.value)} />
                  </Field>
                  <Field label="Email for job enquiries">
                    <Input value={form['company.email'] || ''} onChange={(e) => set('company.email', e.target.value)} />
                  </Field>
                  <Field label="Phone number for jobs">
                    <Input value={form['company.phone'] || ''} onChange={(e) => set('company.phone', e.target.value)} />
                  </Field>
                  <Field label="Tax number">
                    <Input className="mono" value={form['company.ein'] || ''} onChange={(e) => set('company.ein', e.target.value)} />
                  </Field>
                  <Field label="Office address">
                    <Input value={form['company.address'] || ''} onChange={(e) => set('company.address', e.target.value)} />
                  </Field>
                </div>
              </CardBody>
            </Card>
          ) : null}

          {section === 'ops' ? (
            <Card>
              <CardHead title="How we work" subtitle="The starting points we use when finding people for jobs" icon={Sliders} />
              <CardBody className="col gap-20">
                <div className="grid grid-2" style={{ gap: 16 }}>
                  <Field label="How far we look for lumpers (miles)">
                    <Input className="mono" value={form['ops.defaultRadiusMiles'] || ''} onChange={(e) => set('ops.defaultRadiusMiles', e.target.value)} />
                  </Field>
                  <Field label="How long they get to reply (minutes)" hint="How long a lumper has to respond before it re-broadcasts">
                    <Input className="mono" value={form['ops.offerWindowMinutes'] || ''} onChange={(e) => set('ops.offerWindowMinutes', e.target.value)} />
                  </Field>
                  <Field label="Only our best for rush jobs (rating)">
                    <Input className="mono" value={form['ops.minRatingForHotLoads'] || ''} onChange={(e) => set('ops.minRatingForHotLoads', e.target.value)} />
                  </Field>
                  <Field label="Bonus for crew leads">
                    <Input className="mono" value={form['ops.crewLeadBonus'] || ''} onChange={(e) => set('ops.crewLeadBonus', e.target.value)} />
                  </Field>
                </div>
                <Field label="Where we work" hint="Separate each place with a comma">
                  <Input
                    value={(form['ops.serviceAreas'] || '').replace(/^\[|\]$/g, '').replace(/"/g, '')}
                    onChange={(e) => set('ops.serviceAreas', JSON.stringify(e.target.value.split(',').map((s) => s.trim()).filter(Boolean)))}
                  />
                </Field>
                <div className="col gap-12">
                  <Switch
                    label="Pick the best person for me when I add a job"
                    checked={form['ops.autoAssign'] === 'true'}
                    onChange={(e) => set('ops.autoAssign', e.target.checked ? 'true' : 'false')}
                  />
                  <Switch
                    label="Jobs need a photo before they can be finished"
                    checked={form['ops.requirePhotos'] === 'true'}
                    onChange={(e) => set('ops.requirePhotos', e.target.checked ? 'true' : 'false')}
                  />
                  <Switch
                    label="Lumpers have to be at the dock to check in"
                    checked={form['ops.requireGeofence'] === 'true'}
                    onChange={(e) => set('ops.requireGeofence', e.target.checked ? 'true' : 'false')}
                  />
                </div>
              </CardBody>
            </Card>
          ) : null}

          {section === 'pay' ? (
            <Card>
              <CardHead title="Paying lumpers" subtitle="When and how lumpers get paid" icon={Wallet} />
              <CardBody className="col gap-16">
                <div className="grid grid-2" style={{ gap: 16 }}>
                  <Field label="Pay cycle">
                    <Select
                      value={form['pay.cycle'] || 'weekly'}
                      onChange={(e) => set('pay.cycle', e.target.value)}
                      options={[
                        { value: 'daily', label: 'Daily' },
                        { value: 'weekly', label: 'Weekly' },
                        { value: 'biweekly', label: 'Every two weeks' },
                        { value: 'on_demand', label: 'On demand' },
                      ]}
                    />
                  </Field>
                  <Field label="Pay day">
                    <Select
                      value={form['pay.payDay'] || 'friday'}
                      onChange={(e) => set('pay.payDay', e.target.value)}
                      options={['monday', 'tuesday', 'wednesday', 'thursday', 'friday'].map((d) => ({ value: d, label: titleCase(d) }))}
                    />
                  </Field>
                  <Field label="Per-payout processing fee" hint="Deducted from each lumper payout">
                    <Input className="mono" value={form['pay.processingFee'] || ''} onChange={(e) => set('pay.processingFee', e.target.value)} />
                  </Field>
                  <Field label="Instant pay fee (%)">
                    <Input className="mono" value={form['pay.fastPayFeePct'] || ''} onChange={(e) => set('pay.fastPayFeePct', e.target.value)} />
                  </Field>
                </div>
                <Callout tone="blue" icon={Wallet}>
                  Processing fees are applied per lumper per run. Fast-pay fees apply only to same-day payouts.
                </Callout>
              </CardBody>
            </Card>
          ) : null}

          {section === 'billing' ? (
            <Card>
              <CardHead title="Charging customers" subtitle="Terms and accessorial pricing" icon={Receipt} />
              <CardBody>
                <div className="grid grid-2" style={{ gap: 16 }}>
                  <Field label="Usual payment terms">
                    <Select
                      value={form['billing.terms'] || 'net30'}
                      onChange={(e) => set('billing.terms', e.target.value)}
                      options={[
                        { value: 'due_on_receipt', label: 'Due on receipt' },
                        { value: 'net15', label: 'Net 15' },
                        { value: 'net30', label: 'Net 30' },
                        { value: 'net45', label: 'Net 45' },
                        { value: 'net60', label: 'Net 60' },
                      ]}
                    />
                  </Field>
                  <Field label="Fuel surcharge (%)">
                    <Input className="mono" value={form['billing.fuelSurchargePct'] || ''} onChange={(e) => set('billing.fuelSurchargePct', e.target.value)} />
                  </Field>
                  <Field label="After-hours dock fee">
                    <Input className="mono" value={form['billing.afterHoursFee'] || ''} onChange={(e) => set('billing.afterHoursFee', e.target.value)} />
                  </Field>
                  <Field label="Charge for waiting, per hour">
                    <Input className="mono" value={form['billing.detentionPerHour'] || ''} onChange={(e) => set('billing.detentionPerHour', e.target.value)} />
                  </Field>
                </div>
              </CardBody>
            </Card>
          ) : null}

          {section === 'billing' ? <PaymentProcessorsCard /> : null}

          {section === 'rates' ? (
            <Card>
              <CardHead
                title="Our price list"
                subtitle={`${rateCards.length} cards · ${rateCards.filter((c) => c.kind === 'lumper').length} for lumper pay`}
                icon={Table2}
                actions={<Button size="sm" variant="outline" onClick={() => setRateCard({ kind: 'customer' })}>New rate card</Button>}
              />
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Price list</th>
                      <th className="narrow">Type</th>
                      <th className="right">Per pallet</th>
                      <th className="right">Per hour</th>
                      <th className="right">Per case</th>
                      <th className="right">Min hours</th>
                      <th className="right">After hrs ×</th>
                      <th className="right">Weekend ×</th>
                      <th className="right">Customers</th>
                      <th className="right">Jobs · 30d</th>
                      <th className="narrow right" />
                    </tr>
                  </thead>
                  <tbody>
                    {rateCards.map((card) => (
                      <tr key={card.id}>
                        <td>
                          <div className="col gap-2">
                            <span className="mono" style={{ fontSize: 'var(--fs-sm)', fontWeight: 600 }}>{card.name}</span>
                            <span className="dimmer" style={{ fontSize: 10 }}>updated {relative(card.updatedAt)}</span>
                          </div>
                        </td>
                        <td className="narrow">
                          <Badge tone={card.kind === 'lumper' ? 'blue' : 'muted'} size="sm">{card.kind}</Badge>
                        </td>
                        <td className="right num">{money(card.perPallet, { decimals: 2 })}</td>
                        <td className="right num">{money(card.perHour, { decimals: 0 })}</td>
                        <td className="right num">{card.perCase ? money(card.perCase, { decimals: 2 }) : '—'}</td>
                        <td className="right num">{num(card.minimumHours, 1)}</td>
                        <td className="right num">{Number(card.afterHoursMultiplier).toFixed(2)}</td>
                        <td className="right num">{Number(card.weekendMultiplier).toFixed(2)}</td>
                        <td className="right num">{card.customerCount || '—'}</td>
                        <td className="right num">{card.jobs30d || '—'}</td>
                        <td className="narrow right">
                          <Button size="sm" variant="ghost" onClick={() => setRateCard(card)}>Edit</Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          ) : null}

          {section === 'brand' ? (
            <Card>
              <CardHead title="Brand" subtitle="Accent colours used across the console and the app" icon={Palette} />
              <CardBody className="col gap-16">
                <div className="grid grid-2" style={{ gap: 16 }}>
                  <Field label="Primary accent">
                    <div className="row gap-10">
                      <input
                        type="color"
                        value={form['brand.accent'] || '#ff8a1f'}
                        onChange={(e) => set('brand.accent', e.target.value)}
                        style={{ width: 46, height: 34, borderRadius: 8, border: '1px solid var(--line-strong)', background: 'transparent' }}
                      />
                      <Input className="mono" value={form['brand.accent'] || ''} onChange={(e) => set('brand.accent', e.target.value)} />
                    </div>
                  </Field>
                  <Field label="Hazard accent">
                    <div className="row gap-10">
                      <input
                        type="color"
                        value={form['brand.hazard'] || '#ffe14d'}
                        onChange={(e) => set('brand.hazard', e.target.value)}
                        style={{ width: 46, height: 34, borderRadius: 8, border: '1px solid var(--line-strong)', background: 'transparent' }}
                      />
                      <Input className="mono" value={form['brand.hazard'] || ''} onChange={(e) => set('brand.hazard', e.target.value)} />
                    </div>
                  </Field>
                </div>
                <div className="hazard-rule" />
                <Callout tone="blue" icon={Palette}>
                  These values are stored for the mobile app theme and marketing site. The console palette is fixed for contrast and safety.
                </Callout>
              </CardBody>
            </Card>
          ) : null}

          {section === 'team' ? <TeamSection /> : null}

          {section === 'account' ? <AccountSection /> : null}

          {section === 'data' ? (
            <div className="col gap-20">
              <Card>
                <CardHead title="What is in here" icon={Database} />
                <CardBody>
                  <div className="grid grid-4">
                    {[
                      ['Users', counts.users],
                      ['Lumpers', counts.lumpers],
                      ['Customers', counts.customers],
                      ['Facilities', counts.sites],
                      ['Jobs', counts.jobs],
                      ['Messages', counts.notifications],
                    ].map(([label, value]) => (
                      <div key={label} className="tile">
                        <span className="label">{label}</span>
                        <div className="stat-value sm">{num(value)}</div>
                      </div>
                    ))}
                  </div>
                </CardBody>
              </Card>

            </div>
          ) : null}
        </div>
      </div>

      <RateCardModal
        card={rateCard}
        onClose={() => setRateCard(null)}
        onSaved={() => {
          setRateCard(null);
          refresh();
          toast.ok('Price saved');
        }}
      />

    </>
  );
}

/**
 * Changing your own password.
 *
 * Nothing here talks to a reset email — there is no mail server on this box.
 * The signed-in person proves it is them by typing the password they have now.
 * If somebody is locked out completely, whoever runs the server can set a new
 * one with `npm run reset-password -- <their email>`.
 */
function AccountSection() {
  const toast = useToast();
  const { actor, profile } = useAuth();
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const set = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const submit = async () => {
    if (!form.currentPassword) {
      toast.err('Type the password you use now', 'It is how we know it is really you.');
      return;
    }
    if (form.newPassword.length < 6) {
      toast.err('Your new password needs at least 6 characters');
      return;
    }
    if (form.newPassword !== form.confirm) {
      toast.err('The two new passwords do not match');
      return;
    }
    if (form.newPassword === form.currentPassword) {
      toast.err('That is the same as your old password');
      return;
    }
    setBusy(true);
    try {
      await api.post('/auth/change-password', {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      setForm({ currentPassword: '', newPassword: '', confirm: '' });
      toast.ok('Password changed', 'Use the new one the next time you sign in.');
    } catch (err) {
      toast.err('Could not change it', err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="col gap-20">
      <Card>
        <CardHead
          title="Your password"
          subtitle={profile?.email ? `The password for ${profile.email}` : 'The password you sign in with'}
          icon={ShieldCheck}
        />
        <CardBody>
          <div className="grid grid-2" style={{ gap: 16, maxWidth: 620 }}>
            <Field label="The password you use now" hint="Nobody else can see this.">
              <Input
                type="password"
                autoComplete="current-password"
                value={form.currentPassword}
                onChange={set('currentPassword')}
              />
            </Field>
            <div />
            <Field label="New password" hint="At least 6 characters. Something you will remember on a phone.">
              <Input
                type="password"
                autoComplete="new-password"
                value={form.newPassword}
                onChange={set('newPassword')}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
              />
            </Field>
            <Field label="Type the new one again">
              <Input
                type="password"
                autoComplete="new-password"
                value={form.confirm}
                onChange={set('confirm')}
                onKeyDown={(e) => e.key === 'Enter' && submit()}
              />
            </Field>
          </div>
          <div className="row gap-10 mt-16">
            <Button variant="primary" loading={busy} onClick={submit} disabled={!form.newPassword || !form.confirm}>
              Change my password
            </Button>
            <span className="dim" style={{ fontSize: 'var(--fs-xs)' }}>
              {actor?.name ? `Signed in as ${actor.name}` : null}
            </span>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHead title="If you get locked out" icon={ShieldAlert} />
        <CardBody>
          <p className="muted" style={{ fontSize: 'var(--fs-sm)' }}>
            There is no “forgot my password” email, because this server does not send mail. If you cannot get in,
            whoever runs the server can set a new password from the command line:
          </p>
          <pre
            className="mono"
            style={{
              marginTop: 12,
              padding: '10px 12px',
              background: 'var(--bg-2)',
              border: '1px solid var(--line-strong)',
              borderRadius: 10,
              fontSize: 'var(--fs-sm)',
              overflowX: 'auto',
            }}
          >
            npm run reset-password -- {profile?.email || 'you@example.com'}
          </pre>
          <p className="dim mt-12" style={{ fontSize: 'var(--fs-xs)' }}>
            It prints a new password once and writes the change into the history so you can see when it happened.
            Change it to something you like on this page straight after.
          </p>
        </CardBody>
      </Card>
    </div>
  );
}

function TeamSection() {
  const toast = useToast();
  const { data, refresh } = useApi('/settings/users');
  const [userOpen, setUserOpen] = useState(false);
  const users = data || [];

  const update = async (user, patch) => {
    try {
      await api.patch(`/settings/users/${user.id}`, patch);
      toast.ok(`${user.name} updated`);
      refresh();
    } catch (err) {
      toast.err('Could not update the user', err.message);
    }
  };

  return (
    <>
      <Card>
        <CardHead
          title="Office Staff Users"
          subtitle="Who can get in, and what they are allowed to change"
          icon={Users}
          actions={<Button size="sm" variant="primary" onClick={() => setUserOpen(true)}>Add someone</Button>}
        />
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th>Person</th>
                <th className="narrow">What they can do</th>
                <th className="narrow">Status</th>
                <th className="narrow">Last time they looked</th>
                <th className="right">Changes made</th>
                <th className="narrow right" />
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="row gap-10">
                      <Avatar name={user.name} tone={user.accent || 'amber'} size="sm" />
                      <div className="col gap-2 truncate">
                        <span style={{ fontWeight: 600, fontSize: 'var(--fs-sm)' }}>{user.name}</span>
                        <span className="dim truncate" style={{ fontSize: 'var(--fs-xs)' }}>{user.email} · {user.title}</span>
                      </div>
                    </div>
                  </td>
                  <td className="narrow">
                    <span className="chip">{ROLE_LABELS[user.role] || user.role}</span>
                  </td>
                  <td className="narrow">
                    <Badge tone={user.status === 'active' ? 'green' : 'red'} size="sm">{user.status}</Badge>
                  </td>
                  <td className="narrow dim">{user.lastLoginAt ? relative(user.lastLoginAt) : 'never'}</td>
                  <td className="right num">{num(user.actionCount)}</td>
                  <td className="narrow right">
                    {user.role !== 'owner' ? (
                      <div className="row gap-6 end">
                        <Button size="sm" variant="ghost" onClick={() => update(user, { status: user.status === 'active' ? 'disabled' : 'active' })}>
                          {user.status === 'active' ? 'Disable' : 'Enable'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setEditing(user)}>Edit</Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => api.del(`/settings/users/${user.id}`).then(refresh).catch((err) => toast.err('Could not delete', err.message))}
                        >
                          Remove
                        </Button>
                      </div>
                    ) : (
                      <Badge tone="amber" size="sm">protected</Badge>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <UserModal open={userOpen} onClose={() => setUserOpen(false)} onSaved={() => { setUserOpen(false); refresh(); }} />
      {editing ? (
        <UserModal
          key={editing.id}
          open
          user={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); refresh(); }}
        />
      ) : null}
    </>
  );
}

/**
 * One dialog for adding somebody to the office team and for changing them
 * afterwards: their name, what they do, their role, and a new password if they
 * need one. A role has to be changeable after the fact — somebody hired to run
 * the day often ends up doing the billing too.
 */
function UserModal({ open, user = null, onClose, onSaved }) {
  const toast = useToast();
  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    title: user?.title || '',
    password: '',
    role: user?.role || 'ops',
    accent: user?.accent || 'blue',
  });
  const [busy, setBusy] = useState(false);
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
  const isOwnerRow = user?.role === 'owner';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={user ? `Edit ${user.name}` : 'Add someone to the office team'}
      subtitle={user ? 'Change their details, their role, or their password' : 'They can sign in straight away with this password'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              if (!form.name || !form.email || (!user && !form.password)) {
                toast.err(user ? 'Name and email are required' : 'Name, email and a password are required');
                return;
              }
              if (form.password && form.password.length < 6) {
                toast.err('A password needs at least 6 characters');
                return;
              }
              setBusy(true);
              try {
                if (user) {
                  const patch = { name: form.name, email: form.email, title: form.title, accent: form.accent };
                  if (!isOwnerRow) patch.role = form.role;
                  if (form.password) patch.password = form.password;
                  await api.patch(`/settings/users/${user.id}`, patch);
                  toast.ok(`${form.name} updated`);
                } else {
                  await api.post('/settings/users', form);
                  toast.ok(`${form.name} can sign in now`);
                }
                onSaved();
              } catch (err) {
                toast.err(user ? 'Could not save that' : 'Could not add the user', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            {user ? 'Save' : 'Create account'}
          </Button>
        </>
      }
    >
      <div className="grid grid-2" style={{ gap: 14 }}>
        <Field label="Their name" required><Input value={form.name} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Title"><Input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Warehouse manager" /></Field>
        <Field label="Email" required><Input value={form.email} onChange={(e) => set('email', e.target.value)} /></Field>
        <Field
          label={user ? 'A new password' : 'A password for them'}
          hint={user ? 'Leave it empty to keep the one they have' : undefined}
          required={!user}
        >
          <Input value={form.password} onChange={(e) => set('password', e.target.value)} />
        </Field>
        <Field label="Role">
          {isOwnerRow ? (
            <>
              <Input value="Owner — everything" disabled />
              <Helper>The owner account cannot be demoted from here.</Helper>
            </>
          ) : (
            <Select
              value={form.role}
              onChange={(e) => set('role', e.target.value)}
              options={[
                { value: 'ops', label: 'Runs the day-to-day work' },
                { value: 'billing', label: 'Handles invoices and pay' },
                { value: 'viewer', label: 'Can look but not change anything' },
                { value: 'owner', label: 'Owner — everything' },
              ]}
            />
          )}
        </Field>
        <Field label="Avatar colour">
          <Select
            value={form.accent}
            onChange={(e) => set('accent', e.target.value)}
            options={['amber', 'blue', 'lime', 'violet', 'teal', 'red'].map((c) => ({ value: c, label: titleCase(c) }))}
          />
        </Field>
      </div>
    </Modal>
  );
}

function RateCardModal({ card, onClose, onSaved }) {
  const toast = useToast();
  const isNew = card && !card.id;
  const [form, setForm] = useState(card || {});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm(card || {});
  }, [card]);

  if (!card) return null;
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Modal
      open
      onClose={onClose}
      size="wide"
      title={isNew ? 'New rate card' : `Edit ${card.name}`}
      subtitle="Rates drive what lumpers are paid and what customers are billed"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            loading={busy}
            onClick={async () => {
              setBusy(true);
              try {
                const payload = { ...form };
                ['perPallet', 'perHour', 'perCase', 'flatFee', 'minimumHours', 'afterHoursMultiplier', 'weekendMultiplier', 'sortFee', 'waitFeePerHour'].forEach((key) => {
                  if (payload[key] !== undefined) payload[key] = Number(payload[key]);
                });
                if (isNew) await api.post('/settings/rate-cards', payload);
                else await api.patch(`/settings/rate-cards/${card.id}`, payload);
                onSaved();
              } catch (err) {
                toast.err('Could not save the rate card', err.message);
              } finally {
                setBusy(false);
              }
            }}
          >
            Save rate card
          </Button>
        </>
      }
    >
      <div className="grid grid-2" style={{ gap: 14 }}>
        <Field label="Name" required><Input value={form.name || ''} onChange={(e) => set('name', e.target.value)} /></Field>
        <Field label="Type">
          <Select
            value={form.kind || 'customer'}
            onChange={(e) => set('kind', e.target.value)}
            options={[
              { value: 'customer', label: 'Customer billing' },
              { value: 'lumper', label: 'Lumper pay' },
            ]}
          />
        </Field>
        <Field label="Price per pallet"><Input className="mono" type="number" step="0.05" value={form.perPallet ?? ''} onChange={(e) => set('perPallet', e.target.value)} /></Field>
        <Field label="Price per hour"><Input className="mono" type="number" step="0.5" value={form.perHour ?? ''} onChange={(e) => set('perHour', e.target.value)} /></Field>
        <Field label="Per case"><Input className="mono" type="number" step="0.01" value={form.perCase ?? ''} onChange={(e) => set('perCase', e.target.value)} /></Field>
        <Field label="Flat fee"><Input className="mono" type="number" value={form.flatFee ?? ''} onChange={(e) => set('flatFee', e.target.value)} /></Field>
        <Field label="Minimum hours"><Input className="mono" type="number" step="0.5" value={form.minimumHours ?? ''} onChange={(e) => set('minimumHours', e.target.value)} /></Field>
        <Field label="After-hours multiplier"><Input className="mono" type="number" step="0.05" value={form.afterHoursMultiplier ?? ''} onChange={(e) => set('afterHoursMultiplier', e.target.value)} /></Field>
        <Field label="Weekend multiplier"><Input className="mono" type="number" step="0.05" value={form.weekendMultiplier ?? ''} onChange={(e) => set('weekendMultiplier', e.target.value)} /></Field>
        <Field label="Wait fee per hour"><Input className="mono" type="number" value={form.waitFeePerHour ?? ''} onChange={(e) => set('waitFeePerHour', e.target.value)} /></Field>
        <Field label="Sort fee"><Input className="mono" type="number" value={form.sortFee ?? ''} onChange={(e) => set('sortFee', e.target.value)} /></Field>
        <Field label="Notes" className="span-2"><Textarea rows={2} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} /></Field>
      </div>
    </Modal>
  );
}
